import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Search, Pill, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = "all";

export default function Products() {
  const [active, setActive] = useState(ALL);
  const [query, setQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { hash } = useLocation();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/catalog`);
        if (!mounted) return;
        setCategories(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (mounted) setCategories([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      if (categories.find((c) => c.id === id)) {
        setActive(id);
      }
    }
  }, [hash, categories]);

  const visible = useMemo(() => {
    const list =
      active === ALL ? categories : categories.filter((c) => c.id === active);
    if (!query.trim()) return list;
    const q = query.toLowerCase();
    return list
      .map((cat) => ({
        ...cat,
        products: cat.products.filter(
          (p) =>
            p.name.toLowerCase().includes(q) ||
            p.composition.toLowerCase().includes(q) ||
            p.indication.toLowerCase().includes(q),
        ),
      }))
      .filter((cat) => cat.products.length > 0);
  }, [active, query, categories]);

  const totalProducts = categories.reduce(
    (n, c) => n + c.products.length,
    0,
  );

  return (
    <div data-testid="page-products" className="bg-[#F8F9F7]">
      {/* INTRO */}
      <section className="border-b border-[#E0E5E2] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <p className="overline text-[#1E8A2E]">Product catalog</p>
              <h1 className="font-serif mt-4 text-5xl sm:text-6xl md:text-7xl tracking-tight text-[#121413] leading-[0.95]">
                A portfolio built around{" "}
                <span className="italic text-[#D92D2D]">patient need</span>.
              </h1>
              <p className="mt-6 max-w-2xl text-[#4A524D] leading-relaxed">
                Browse our {totalProducts}+ formulations across major
                therapeutic areas. Each product is manufactured under WHO-GMP
                conditions and undergoes strict quality assurance.
              </p>
            </div>
            <div className="md:col-span-4">
              <div className="border border-[#E0E5E2] bg-white p-6">
                <p className="overline text-[#7A827D]">Important</p>
                <p className="mt-3 text-sm text-[#4A524D] leading-relaxed">
                  This catalog is for informational purposes only. Medicines
                  should be taken under qualified medical supervision. For
                  detailed prescribing information please contact our medical
                  team.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATALOG */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* SIDEBAR */}
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-28">
                <div className="relative">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A827D]"
                  />
                  <input
                    type="text"
                    placeholder="Search products"
                    data-testid="product-search-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-3 bg-white border border-[#E0E5E2] text-sm placeholder:text-[#7A827D] focus:outline-none focus:border-[#1E8A2E] rounded-sm"
                  />
                </div>

                <p className="overline text-[#7A827D] mt-8 mb-4">Categories</p>
                <nav className="flex flex-col">
                  <button
                    data-testid="product-category-all"
                    onClick={() => setActive(ALL)}
                    className={`text-left py-3 border-b border-[#E0E5E2] text-sm transition-colors ${
                      active === ALL
                        ? "text-[#121413] font-semibold"
                        : "text-[#4A524D] hover:text-[#121413]"
                    }`}
                  >
                    <span className="flex items-center justify-between">
                      <span>All products</span>
                      <span className="text-xs text-[#7A827D]">
                        {totalProducts}
                      </span>
                    </span>
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      data-testid={`product-category-${cat.id}`}
                      onClick={() => setActive(cat.id)}
                      className={`text-left py-3 border-b border-[#E0E5E2] text-sm transition-colors ${
                        active === cat.id
                          ? "text-[#121413] font-semibold"
                          : "text-[#4A524D] hover:text-[#121413]"
                      }`}
                    >
                      <span className="flex items-center justify-between">
                        <span>{cat.name}</span>
                        <span className="text-xs text-[#7A827D]">
                          {cat.products.length}
                        </span>
                      </span>
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* MAIN */}
            <div className="lg:col-span-9">
              {loading ? (
                <div
                  data-testid="products-loading-state"
                  className="border border-[#E0E5E2] bg-white p-12 text-center"
                >
                  <Loader2
                    size={28}
                    className="mx-auto text-[#1E8A2E] animate-spin"
                  />
                  <p className="mt-5 font-serif text-2xl text-[#121413]">
                    Loading catalog…
                  </p>
                </div>
              ) : categories.length === 0 ? (
                <div
                  data-testid="products-empty-catalog"
                  className="border border-[#E0E5E2] bg-white p-12 text-center"
                >
                  <p className="font-serif text-3xl text-[#121413]">
                    Our product catalog is being updated.
                  </p>
                  <p className="mt-3 text-sm text-[#4A524D] max-w-md mx-auto">
                    Please check back shortly or get in touch for the latest
                    product list.
                  </p>
                </div>
              ) : visible.length === 0 ? (
                <div
                  data-testid="products-empty-state"
                  className="border border-[#E0E5E2] bg-white p-12 text-center"
                >
                  <p className="font-serif text-3xl text-[#121413]">
                    No products match your search.
                  </p>
                  <p className="mt-3 text-sm text-[#4A524D]">
                    Try a different keyword or pick another category.
                  </p>
                </div>
              ) : (
                visible.map((cat) => (
                  <div
                    key={cat.id}
                    id={cat.id}
                    data-testid={`category-section-${cat.id}`}
                    className="mb-16 scroll-mt-28"
                  >
                    <div className="flex items-end justify-between border-b border-[#121413] pb-4">
                      <div>
                        <p className="overline text-[#1E8A2E]">
                          {cat.products.length} products
                        </p>
                        <h2 className="font-serif text-4xl md:text-5xl mt-2 text-[#121413] tracking-tight">
                          {cat.name}
                        </h2>
                      </div>
                    </div>
                    <p className="mt-4 max-w-2xl text-[#4A524D] text-sm leading-relaxed">
                      {cat.description}
                    </p>

                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E0E5E2] border border-[#E0E5E2]">
                      {cat.products.map((p, i) => (
                        <div
                          key={p.name}
                          data-testid={`product-card-${cat.id}-${i}`}
                          className="bg-white p-7 flex flex-col group"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <span className="inline-flex w-9 h-9 items-center justify-center rounded-full bg-[#F1F3F0] text-[#1E8A2E]">
                                <Pill size={16} />
                              </span>
                              <p className="overline text-[#7A827D]">
                                {p.dosage_form}
                              </p>
                            </div>
                          </div>
                          <h3 className="font-serif text-2xl mt-5 text-[#121413] tracking-tight">
                            {p.name}
                          </h3>
                          <p className="mt-2 text-sm text-[#4A524D] leading-relaxed">
                            {p.composition}
                          </p>
                          <div className="mt-5 pt-4 border-t border-[#E0E5E2]">
                            <p className="overline text-[#D92D2D]">
                              Indication
                            </p>
                            <p className="mt-2 text-sm text-[#121413]">
                              {p.indication}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
